import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import API from '../../../services/API'
import { GlobalContext, UserContext } from '../../../App'

const BASE = '/make-ready-load-lists'

const getSectionCount = (list) =>
  Array.isArray(list?.sections) ? list.sections.length : 0

const getConsumableCount = (list) =>
  Array.isArray(list?.sections)
    ? list.sections.reduce(
        (total, section) =>
          total +
          (Array.isArray(section?.requiredConsumables)
            ? section.requiredConsumables.length
            : 0),
        0,
      )
    : 0

const getAssetCount = (list) => {
  const sectionAssets = Array.isArray(list?.sections)
    ? list.sections.reduce(
        (total, section) =>
          total +
          (Array.isArray(section?.requiredAssets)
            ? section.requiredAssets.length
            : 0),
        0,
      )
    : 0
  return (
    sectionAssets +
    (Array.isArray(list?.requiredAssets) ? list.requiredAssets.length : 0)
  )
}

const LoadLists = () => {
  const [loading, setLoading] = useState(true)
  const [loadLists, setLoadLists] = useState([])
  const [fetchError, setFetchError] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)
  const { user } = useContext(UserContext)
  const canEdit =
    user?.teams?.includes('Governance') ||
    user?.teams?.includes('Manager') ||
    user?.teams?.includes('Supervisor')

  useEffect(() => {
    API.get('/settings/makereadyloadlists?types=all')
      .then((res) => {
        if (res.status === 200) setLoadLists(res.data || [])
        else setFetchError(true)
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  // The summary endpoint has no applies-to count; if the server ever adds
  // one it is picked up here without an N+1 fetch.
  const hasAppliesCount = loadLists.some(
    (l) => typeof l.appliesToCount === 'number',
  )

  const confirmDelete = async () => {
    if (!pendingDelete) return
    setDeleting(true)
    try {
      const res = await API.delete(`/makereadyloadlist/${pendingDelete._id}`)
      if (res.status === 200) {
        setLoadLists((prev) => prev.filter((l) => l._id !== pendingDelete._id))
        setToast({ message: 'Load list deleted' })
      } else {
        setToast({
          message: res.data?.error || 'Unable to delete load list',
          severity: 'error',
        })
      }
    } catch {
      setToast({ message: 'Unable to delete load list', severity: 'error' })
    } finally {
      setDeleting(false)
      setPendingDelete(null)
    }
  }

  const columns = [
    { field: 'title', headerName: 'Title', minWidth: 240, flex: 1 },
    {
      field: 'type',
      headerName: 'Type',
      minWidth: 150,
      flex: 0.35,
      renderCell: (params) => (
        <Chip
          label={params.value || 'Uncategorised'}
          size="small"
          color={params.value === 'Vehicle' ? 'primary' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'sections',
      headerName: 'Sections',
      minWidth: 110,
      flex: 0.25,
      valueGetter: (value, row) => getSectionCount(row),
    },
    {
      field: 'consumables',
      headerName: 'Consumables',
      minWidth: 130,
      flex: 0.3,
      valueGetter: (value, row) => getConsumableCount(row),
    },
    {
      field: 'assets',
      headerName: 'Expected assets',
      minWidth: 140,
      flex: 0.3,
      valueGetter: (value, row) => getAssetCount(row),
    },
    ...(hasAppliesCount
      ? [
          {
            field: 'appliesToCount',
            headerName: 'Applies to',
            minWidth: 110,
            flex: 0.25,
            valueGetter: (value) => (typeof value === 'number' ? value : ''),
          },
        ]
      : []),
    {
      field: 'actions',
      headerName: '',
      width: canEdit ? 96 : 56,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row">
          <Tooltip title="Duplicate as a new list">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                navigate(`${BASE}/new?cloneFrom=${params.row._id}`)
              }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canEdit && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation()
                  setPendingDelete(params.row)
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ]

  return (
    <Stack gap={2}>
      <Stack
        direction="row"
        gap={1}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <Stack direction="row" justifyContent="space-between" width="100%">
          <Stack>
            <Typography variant="title2" fontSize="1.2rem">
              Load Lists
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Build the vehicle, bag, and device checklists used during Make
              Ready.
            </Typography>
          </Stack>
          <Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/make-ready-load-lists/new')}
            >
              Create New
            </Button>
          </Box>
        </Stack>
        {canEdit && (
          <Stack direction="row" gap={1}>
            <Button
              onClick={() => navigate(`${BASE}/setup`)}
              size="small"
              variant="outlined"
              startIcon={<AutoFixHighIcon />}
            >
              Setup wizard
            </Button>
            <Button
              onClick={() => navigate(`${BASE}/new`)}
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
            >
              New load list
            </Button>
          </Stack>
        )}
      </Stack>

      {fetchError && (
        <Alert severity="error" variant="filled">
          Unable to fetch Load Lists. Please try again later.
        </Alert>
      )}

      <Stack direction="row" gap={1} flexWrap="wrap">
        <Chip label={`${loadLists.length} lists`} />
        <Chip
          label={`${loadLists.filter((list) => list.type === 'Vehicle').length} vehicle lists`}
        />
        <Chip
          label={`${loadLists.filter((list) => list.type !== 'Vehicle').length} asset lists`}
        />
      </Stack>

      <Paper sx={{ height: 560, width: '100%' }}>
        <CustomDataGrid
          loading={loading}
          columns={columns}
          rows={loadLists}
          getRowId={(list) => list._id}
          disableRowSelectionOnClick
          norowsoverlay={
            <Alert
              severity="info"
              action={
                canEdit ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => navigate(`${BASE}/setup`)}
                  >
                    Start setup
                  </Button>
                ) : null
              }
            >
              No load lists have been created yet.
            </Alert>
          }
          onRowClick={({ row }) => navigate(`${BASE}/${row._id}`)}
        />
      </Paper>

      <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)}>
        <DialogTitle>Delete "{pendingDelete?.title}"?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Any vehicles or assets using this list will be unassigned and will
            need a new list before they can be checked again. This cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={confirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default LoadLists
